import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { AGENT_URL } from '../config/env'
import { messageKeys } from '../lib/queryKeys'
import { fetchJsonWithAuth, requireAuthToken } from '@/lib/api'
import type { ClientMessage } from '../features/messages/types'

export interface PhoneMessagesResponse {
  phone: string
  patient_names: string[]
  messages: ClientMessage[]
}

async function fetchPhoneMessages(
  token: string,
  phone: string,
  signal?: AbortSignal
): Promise<PhoneMessagesResponse> {
  const raw = await fetchJsonWithAuth<{
    phone: string
    patient_names: string[]
    data: ClientMessage[]
  }>({
    token,
    url: `${AGENT_URL}/data/messages?phone=${encodeURIComponent(phone)}`,
    signal,
    errorMessage: 'Failed to fetch messages',
  })
  return {
    phone: typeof raw?.phone === 'string' ? raw.phone : phone,
    patient_names: Array.isArray(raw?.patient_names) ? raw.patient_names : [],
    messages: Array.isArray(raw?.data) ? (raw.data as ClientMessage[]) : [],
  }
}

export function usePhoneMessagesQuery(phone: string | null) {
  const { getToken, userId } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: messageKeys.thread(organization?.id, phone, userId),
    enabled: !!phone && !!organization?.id,
    queryFn: async ({ signal }) => {
      const token = await requireAuthToken(getToken)
      return fetchPhoneMessages(token, phone as string, signal)
    },
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
